import React from "react";

import { Location, Link } from "react-router-dom";
import { Box, Button, Tooltip } from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import dayjs from "dayjs";

import { CommentTextIcon, FileTextIcon } from "assets";
import { Anomaly, getDocumentBills, UtilityBillDTO } from "api";
import { getDateFormat, getInitials, getUTCDateFormat, isNullOrUndefined, setToLocalStorage, toUSNumber } from "utils";
import { Avatar, EMPTY_CELL_VALUE, UtilityBillType, UtilityBillTypeKey } from "components";
import { TABLE_HEADERS } from "consts";

import { EmissionCell } from "./components/table";
import { AnomalyLogCell } from "./components/table/anomaly-log-cell";
import { UTILITY_BILL_PAGE as ROUTE_UTILITY_BILL_PAGE } from "./route-consts";

export const UTILITY_BILLS_PAGE = {
  ABSOLUTE_PATH: "/documents/summary",
  PATH: "summary/*",
  TITLE: "Document summary",
  ICON: <FileTextIcon />,
};

export const UTILITY_BILL_PAGE = ROUTE_UTILITY_BILL_PAGE;

export const UTILITY_BILLS_TABLE = {
  COLUMN_TO_SORT_COLUMN_MAP: {
    amount: "COST",
    co2Emission: "EMISSIONS",
    endDate: "END_DATE",
    startDate: "START_DATE",
    measuredValue: "USAGE",
  },
  INITIAL_SORTING_STATE: [{ id: "startDate", desc: true }],
  ROWS_PER_PAGE_OPTIONS: [100, 200, 500, 1000]
};

// ---- Table Column
export const getBillEntriesColums = ({
  currentUrl,
}: {
  currentUrl: Location;
}) => [
    {
      id: 'items',
      header: '',
      Header: <Box sx={{ height: "24px" }} />,
      muiTableHeadCellProps: {
        sx: {
          fontSize: {
            xs: '16px',
            sm: '16px',
            md: '16px',
            lg: '16px',
            xl: '16px',

          },
        },
      },
      columns: [
        {
          id: "comments",
          header: "",
          size: 80,
          accessorKey: "numberOfComments",
          enableColumnFilter: false,
          Cell: ({ row }: any) => {
            const handleClick = (e: any) => {
              e.stopPropagation();
              setToLocalStorage("dataEntries", JSON.stringify(row));
              setToLocalStorage("type", "Summary");
              window.sessionStorage.setItem("summaryDocumentID", row.original.id);
              getDocumentBills(row.original.documentGuid)
                .then((bills) => {
                  setToLocalStorage("documentbills", JSON.stringify(bills));
                })
                .catch((error) => {
                  console.error("Error fetching document bills:", error);
                });
              setToLocalStorage(
                "Summary_URL",
                currentUrl.pathname + currentUrl.search,
              );

              setToLocalStorage("documentIdForBills", row.original.id);
            };
            return !row.original.isEstimate ? (
              <Link to={`${UTILITY_BILL_PAGE.ABSOLUTE_PATH(row.original.id)}/comments`}>
                <Button
                  sx={{
                    minWidth: "24px",
                    height: "24px",
                    alignItems: "center",
                    display: "flex",
                    justifyContent: "center",
                    paddingRight: "4px",
                  }}
                  onClick={handleClick}
                >
                  <CommentTextIcon />
                </Button>
              </Link>
            ) : (EMPTY_CELL_VALUE);
          },
        },
        {
          header: "Customer Site ID",
          accessorKey: "customerSiteId",
          Cell: ({ cell }: any) => {
            const value = cell.getValue() as string;
            if (isNullOrUndefined(value)) {
              return <Box>{EMPTY_CELL_VALUE}</Box>;
            }
            return (
              <Box
                sx={{
                  alignItems: "center",
                  textOverflow: "ellipsis",
                  whiteSpace: "normal",
                  fontSize: "14px",
                  fontWeight: 400,
                  fontFamily: "Manrope",
                }}
              >
                {value}
              </Box>
            );
          },
        },
        {
          Header: <Box sx={{ paddingLeft: "2px" }}>Business Unit</Box>,
          accessorKey: "businessUnit",
          header: "Business Unit",
          Cell: ({ cell }: any) => {
            const value = cell.getValue() as string;

            if (!value) {
              return <Box sx={{ paddingLeft: "15px" }}>{EMPTY_CELL_VALUE}</Box>;
            }
            return (
              <Tooltip title={value}>
                <Box>{value}</Box>
              </Tooltip>
            );
          },
        },
        {
          header: "Site",
          Header: <Box> Site</Box>,
          accessorKey: "siteName",
          Cell: ({ cell }: any) => {
            const value = cell.getValue() as string;

            return (
              <Tooltip title={value}>
                <Box
                  sx={{
                    alignItems: "center",
                    Width: "25px",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                  }}
                >
                  {value}
                </Box>
              </Tooltip>
            );
          },
          size: 180,
        },
        {
          header: "Type",
          Header: <Box sx={{ paddingLeft: "2px" }}>Type</Box>,
          accessorKey: "type",
          Cell: ({ cell }: any) => {
            const value = cell.getValue() as UtilityBillTypeKey;

            if (isNullOrUndefined(value)) {
              return EMPTY_CELL_VALUE;
            }

            return (
              <Tooltip title={value}>
                <Box
                  sx={{
                    alignItems: "center",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                  }}
                >
                  {value}
                </Box>
              </Tooltip>
            );
          },
          size: 150,
        },
        {
          header: "subType",
          Header: <Box>Sub-Type</Box>,
          accessorKey: "subtype",
          Cell: ({ cell }: any) => {
            const value = cell.getValue() as any;

            if (isNullOrUndefined(value)) {
              return EMPTY_CELL_VALUE;
            }

            return <UtilityBillType type={value} />;
          },
          size: 130,
        },
        {
          header: "Provider",
          Header: <Box>Source</Box>,
          accessorKey: "provider",
          Cell: ({ cell }: any) => {
            const value = cell.getValue() as string;

            if (!value) {
              return <Box sx={{ paddingLeft: "15px" }}>{EMPTY_CELL_VALUE}</Box>;
            }
            return (
              <Tooltip title={value}>
                <Box
                  sx={{
                    alignItems: "center",
                    Width: "25px",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                  }}
                >
                  {value}
                </Box>
              </Tooltip>
            );
          },
          enableSorting: false,
          size: 125,
        },
        {
          header: TABLE_HEADERS.accountNumber,
          Header: <Box>{TABLE_HEADERS.accountNumber}</Box>,
          accessorKey: "accountNumber",
          Cell: ({ cell }: any) => {
            const value = cell.getValue() as string;

            if (!value) {
              return <Box sx={{ paddingLeft: "8px" }}>{EMPTY_CELL_VALUE}</Box>;
            }

            return (
              <Tooltip title={value}>
                <Box
                  sx={{
                    alignItems: "center",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                  }}
                >
                  {value}
                </Box>
              </Tooltip>
            );
          },
          size: 122,
        },
        {
          header: TABLE_HEADERS.meterNumber,
          Header: <Box>{TABLE_HEADERS.meterNumber}</Box>,
          accessorKey: "meterNumber",
          Cell: ({ cell }: any) => {
            const value = cell.getValue() as string;

            if (!value) {
              return <Box sx={{ paddingLeft: "8px" }}>{EMPTY_CELL_VALUE}</Box>;
            }

            return (
              <Tooltip title={value}>
                <Box
                  sx={{
                    alignItems: "center",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                  }}
                >
                  {value}
                </Box>
              </Tooltip>
            );
          },
          size: 122,
        },
        {
          header: "GHG Emission",
          Header: <div>GHG Emission</div>,
          accessorKey: "scope",
          Cell: ({ cell }: any) => {
            const value = cell.getValue() as string;

            if (!value) {
              return <Box sx={{ paddingLeft: "18px" }}>{EMPTY_CELL_VALUE}</Box>;
            }

            return (
              <Tooltip title={value}>
                <Box
                  sx={{
                    alignItems: "center",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    paddingLeft: "2px",
                  }}
                >
                  {value}
                </Box>
              </Tooltip>
            );
          },
          size: 160,
        },
        {
          accessorKey: "addedBy",
          Header: <Box>Added By</Box>,
          header: TABLE_HEADERS.uploader,
          Cell: ({ cell }: any) => {
            const value = cell.getValue() as UtilityBillDTO["addedBy"];

            if (!value) {
              return EMPTY_CELL_VALUE;
            }
            return (
              <Tooltip title={value}>
                <Box>
                  <Avatar
                    sx={{
                      width: "32px",
                      height: "32px",
                      paddingLeft: "5px",
                    }}
                  >
                    {getInitials(value)}
                  </Avatar>
                </Box>
              </Tooltip>
            );
          },
          size: 120,
        },
        {
          id: "startDate",
          header: "Start date",
          Header: <Box>Start date</Box>,
          accessorKey: "startDate",
          filterFn: (row: any, id: any, currentFilterValue: any) => {
            const date1 = new Date(currentFilterValue);
            const date2 = new Date(row.getValue(id));
            const date1WithoutTime = new Date(date1.getTime());
            const date2WithoutTime = new Date(date2.getTime());
            const compareData =
              date1WithoutTime.getTime() <= date2WithoutTime.getTime();
            if (compareData) {
              return true;
            }
            return false;
          },
          Filter: ({ column }: any) => {
            const dateValue =
              column.getFilterValue() === undefined
                ? null
                : dayjs(Date.parse(column.getFilterValue()));

            return (
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  value={dateValue}
                  onChange={(newValue: any) => column.setFilterValue(newValue)}
                  format={getUTCDateFormat()}
                  slotProps={{
                    textField: {
                      sx: { minWidth: "120px" },
                      variant: "standard",
                    },
                  }}
                />
              </LocalizationProvider>
            );
          },
          Cell: ({ cell }: any) => {
            const value = cell.getValue();

            if (isNullOrUndefined(value)) {
              return EMPTY_CELL_VALUE;
            }

            return dayjs(value as string).format(getUTCDateFormat());
          },
          size: 140,
        },
        {
          header: "End date",
          Header: <Box>End date</Box>,
          accessorKey: "endDate",
          Filter: ({ column }: any) => {
            const dateValue =
              column.getFilterValue() === undefined
                ? null
                : dayjs(Date.parse(column.getFilterValue()));

            return (
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  value={dateValue}
                  onChange={(newValue: any) => column.setFilterValue(newValue)}
                  format={getUTCDateFormat()}
                  slotProps={{
                    textField: {
                      sx: { minWidth: "120px" },
                      variant: "standard",
                    },
                  }}
                />
              </LocalizationProvider>
            );
          },
          filterFn: (row: any, id: any, currentFilterValue: any) => {
            const date1 = new Date(currentFilterValue);
            const date2 = new Date(row.getValue(id));
            const date1WithoutTime = new Date(date1.getTime());
            const date2WithoutTime = new Date(date2.getTime());

            const compareData = date2WithoutTime <= date1WithoutTime;
            if (compareData) {
              return true;
            }
            return false;
          },
          Cell: ({ cell }: any) => {
            const value = cell.getValue();

            if (isNullOrUndefined(value) || value === 0) {
              return EMPTY_CELL_VALUE;
            }

            return dayjs(value as string).format(getUTCDateFormat());
          },
          size: 140,
        },
        {
          header: "Number of Days",
          Header: <Box>Number of Days</Box>,
          accessorKey: "daysCovered",
          size: 155,
          Cell: ({ cell }: any) => {
            const value = cell.getValue();

            if (isNullOrUndefined(value) || value === 0) {
              return EMPTY_CELL_VALUE;
            }

            return value;
          },
        },
        {
          header: "Usage",
          Header: <Box>Usage</Box>,
          accessorKey: "measuredValue",
          size: 155,

          Cell: ({ cell, row }: any) => {
            const value = cell.getValue() as string;

            return (
              <EmissionCell
                value={value}
                explanation={row.original.unitCalculationFormula}
                unit={row.original.measuredUnit}
                unitvalueshow
              />
            );
          },
        },
        {
          header: "Standardized Usage Value",
          Header: <Box sx={{ paddingLeft: "2px" }}>Standardized Usage Value</Box>,
          accessorKey: "standardizedUsageValue",
          size: 250,
          Cell: ({ cell, row }: any) => {
            const value = cell.getValue();
            if (isNullOrUndefined(value)) {
              return <Box>{EMPTY_CELL_VALUE}</Box>;
            }
            return (
              <Tooltip title={value as string}>
                <Box
                  sx={{
                    alignItems: "center",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    paddingLeft: "4px",
                  }}
                >
                  <span>
                    {toUSNumber(value as string)}{" "}
                    {row.original.standardizedUsageUnit}
                  </span>
                </Box>
              </Tooltip>
            );
          },
        },
        {
          header: "Location Based Emissions",
          Header: <Box sx={{ paddingLeft: "2px" }}>Location Based Emissions</Box>,
          accessorKey: "co2Emission",
          size: 250,
          Cell: ({ cell, row }: any) => {
            const value = cell.getValue() as string;

            const locationEmissionFactorValue = row.original.locationBasedStandardizedEmissionFactorValue ?? "";
            const locationEmissionFactorUnit = row.original.locationBasedStandardizedEmissionFactorUnit ?? "";

            const explanationToAdd = `\n\nLocation CO2e EF: ${locationEmissionFactorValue} ${locationEmissionFactorUnit}`;

            const explanation = String(row.original.explanation ?? "") + explanationToAdd;

            if (isNullOrUndefined(value)) {
              return <Box>{EMPTY_CELL_VALUE}</Box>;
            }
            return (
              <Tooltip title={value as string}>
                <Box
                  sx={{
                    alignItems: "center",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    paddingLeft: "4px",
                  }}
                >
                  <EmissionCell
                    value={value}
                    explanation={explanation}
                  />
                </Box>
              </Tooltip>
            );
          },
        },
      ]
    },
    {
      id: 'Location',
      header: 'Location',
      muiTableHeadCellProps: {
        sx: {
          fontSize: {
            xs: '16px',
            sm: '16px',
            md: '16px',
            lg: '16px',
            xl: '16px',

          },
        },
      },
      columns: [
        {
          Header: <Box sx={{ textOverflow: "ellipsis", whiteSpace: "nowrap" }}>Renewable Usage</Box>,
          header: "Renewable Usage",
          accessorKey: "locationRenewableUsage",
          enableGrouping: false,
          Cell: ({ cell, row }: any) => {
            const value = cell.getValue();
            if (isNullOrUndefined(value)) {
              return EMPTY_CELL_VALUE;
            }

            return <Box>{(value as number)}{' '}{row.original.standardizedUsageUnit}</Box>;
          },
        },
        {
          Header: <Box sx={{ textOverflow: "ellipsis", whiteSpace: "nowrap" }}>Non-Renewable Usage</Box>,
          header: "Non-Renewable Usage",
          accessorKey: "locationNonRenewableUsage",
          enableGrouping: false,
          Cell: ({ cell, row }: any) => {
            const value = cell.getValue();
            if (isNullOrUndefined(value)) {
              return EMPTY_CELL_VALUE;
            }

            return <Box>{(value as number)}{' '}{row.original.standardizedUsageUnit}</Box>;
          },
        },
      ]
    },
    {
      id: 'market-based',
      header: '',
      Header: <Box sx={{ height: "24px" }} />,
      muiTableHeadCellProps: {
        sx: {
          fontSize: {
            xs: '16px',
            sm: '16px',
            md: '16px',
            lg: '16px',
            xl: '16px',

          },
        },
      },
      columns: [
        {
          header: "Market Based Emissions",
          Header: <Box sx={{ paddingLeft: "2px" }}>Market Based Emissions</Box>,
          accessorKey: "marketBasedEmission",
          size: 240,
          Cell: ({ cell, row }: any) => {
            const value = cell.getValue();

            const marketEmissionFactorValue = row.original.marketBasedStandardizedEmissionFactorValue ?? "";
            const marketEmissionFactorUnit = row.original.marketBasedStandardizedEmissionFactorUnit ?? "";

            const explanationToAdd = `\n\nMarket CO2e EF: ${marketEmissionFactorValue} ${marketEmissionFactorUnit}`;

            const explanation = String(row.original.marketExplanation ?? "") + explanationToAdd;

            if (isNullOrUndefined(value)) {
              return <Box>{EMPTY_CELL_VALUE}</Box>;
            }
            return (
              <Tooltip title={value as string}>
                <Box
                  sx={{
                    alignItems: "center",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    paddingLeft: "4px",
                  }}
                >
                  <EmissionCell
                    value={value}
                    explanation={explanation}
                  />
                </Box>
              </Tooltip>
            );
          },
        },
      ]
    },
    {
      id: 'Market',
      header: 'Market',
      muiTableHeadCellProps: {
        sx: {
          fontSize: {
            xs: '16px',
            sm: '16px',
            md: '16px',
            lg: '16px',
            xl: '16px',

          },
        },
      },
      columns: [
        {
          Header: <Box sx={{ textOverflow: "ellipsis", whiteSpace: "nowrap" }}>Renewable Usage</Box>,
          header: "Renewable Usage",
          accessorKey: "marketRenewableUsage",
          enableGrouping: false,
          Cell: ({ cell, row }: any) => {
            const value = cell.getValue();
            if (isNullOrUndefined(value)) {
              return EMPTY_CELL_VALUE;
            }

            return <Box>{(value as number)}{' '}{row.original.standardizedUsageUnit}</Box>;
          },
        },
        {
          Header: <Box sx={{ textOverflow: "ellipsis", whiteSpace: "nowrap" }}>Non-Renewable Usage</Box>,
          header: "Non-Renewable Usage",
          accessorKey: "marketNonRenewableUsage",
          enableGrouping: false,
          Cell: ({ cell, row }: any) => {
            const value = cell.getValue();
            if (isNullOrUndefined(value)) {
              return EMPTY_CELL_VALUE;
            }

            return <Box>{(value as number)}{' '}{row.original.standardizedUsageUnit}</Box>;
          },
        },
      ]
    },
    {
      id: 'item-2',
      header: '',
      Header: <Box sx={{ height: "24px" }} />,
      muiTableHeadCellProps: {
        sx: {
          fontSize: {
            xs: '16px',
            sm: '16px',
            md: '16px',
            lg: '16px',
            xl: '16px',

          },
        },
      },
      columns: [
        {
          header: "Cost",
          Header: <Box>Cost</Box>,
          accessorKey: "amount",
          Cell: ({ cell, row }: any) => {
            const value = cell.getValue();
            return (
              <>
                {!row.original.isEstimate ? (
                  <>
                    {row.original.currency}{" "}
                    {!isNullOrUndefined(value)
                      ? toUSNumber(value as string, true)
                      : EMPTY_CELL_VALUE}
                  </>
                ) : (
                  EMPTY_CELL_VALUE
                )}
              </>
            );
          },
          size: 120,
        },
        {
          header: "Date Created",
          Header: <Box>Date Created</Box>,
          accessorKey: "createDate",
          Cell: ({ cell }: any) => {
            const value = cell.getValue();

            if (isNullOrUndefined(value) || value === 0) {
              return EMPTY_CELL_VALUE;
            }

            return dayjs(value as string)
              .utc()
              .format(getDateFormat());
          },
          size: 160,
        },
        {
          header: "Last Modified Date",
          Header: <Box>Last Modified Date</Box>,
          accessorKey: "updateDate",
          Cell: ({ cell }: any) => {
            const value = cell.getValue();

            if (isNullOrUndefined(value) || value === 0) {
              return EMPTY_CELL_VALUE;
            }

            return dayjs(value as string)
              .utc()
              .format(getDateFormat());
          },
          size: 160,
        },
        {
          header: "Calculation Method",
          Header: <div>Calculation Method</div>,
          accessorKey: "CalculationMethod",
          accessorFn: (row: any) => `${row.billType} - ${row.calculationMethod}`,
          filterFn: "includesString",
          Cell: ({ row }: any) => {
            const value1 = `${row.original.billType} - ${row.original.calculationMethod}`;
            if (!row.original.interExtrapolate) {
              return <Box sx={{ paddingLeft: "18px" }}>{EMPTY_CELL_VALUE}</Box>;
            }

            return (
              <Box
                sx={{
                  alignItems: "center",
                  textOverflow: "ellipsis",
                  whiteSpace: "normal",
                  color: "#44853C",
                  fontSize: "14px",
                  fontWeight: 400,
                  fontFamily: "Manrope",
                }}
              >
                {value1}
              </Box>
            );
          },
          size: 180,
        },
        {
          header: "Edited",
          Header: <div>Edited</div>,
          accessorKey: "edited",
          Cell: ({ cell }: any) => {
            const value = cell.getValue() as string;
            if (isNullOrUndefined(value)) {
              return EMPTY_CELL_VALUE;
            }
            return (
              <Box
                sx={{
                  alignItems: "center",
                  textOverflow: "ellipsis",
                  whiteSpace: "normal",
                  fontSize: "14px",
                  fontWeight: 400,
                  fontFamily: "Manrope",
                }}
              >
                {String(value)}
              </Box>
            );
          },
          size: 180,
        },
        {
          header: "Manually Approved",
          Header: <div>Manually Approved</div>,
          accessorKey: "manuallyApproved",
          Cell: ({ cell }: any) => {
            const value = cell.getValue() as string;
            if (isNullOrUndefined(value)) {
              return EMPTY_CELL_VALUE;
            }
            return (
              <Box
                sx={{
                  alignItems: "center",
                  textOverflow: "ellipsis",
                  whiteSpace: "normal",
                  fontSize: "14px",
                  fontWeight: 400,
                  fontFamily: "Manrope",
                }}
              >
                {String(value)}
              </Box>
            );
          },
          size: 180,
        },
        {
          header: "Duplicate Warning",
          Header: <div>Duplicate Warning</div>,
          accessorKey: "duplicateWarning",
          Cell: ({ cell }: any) => {
            const value = cell.getValue() as string;
            if (isNullOrUndefined(value)) {
              return EMPTY_CELL_VALUE;
            }
            return (
              <Box
                sx={{
                  alignItems: "center",
                  textOverflow: "ellipsis",
                  whiteSpace: "normal",
                  fontSize: "14px",
                  fontWeight: 400,
                  fontFamily: "Manrope",
                }}
              >
                {String(value)}
              </Box>
            );
          },
          size: 180,
        },
        {
          header: "Sequential Threshold Consumption Last Period",
          Header: <div>Sequential Threshold Consumption Last Period</div>,
          accessorKey: "thresholdExceededConsumptionLastPeriod",
          Cell: ({ cell }: any) => {
            const value = cell.getValue() as string;
            if (isNullOrUndefined(value)) {
              return EMPTY_CELL_VALUE;
            }
            return (
              <Box
                sx={{
                  alignItems: "center",
                  textOverflow: "ellipsis",
                  whiteSpace: "normal",
                  fontSize: "14px",
                  fontWeight: 400,
                  fontFamily: "Manrope",
                }}
              >
                {String(value)}
              </Box>
            );
          },
          size: 180,
        },
        {
          header: "Sequential Threshold Cost Last Period",
          Header: <div>Sequential Threshold Cost Last Period</div>,
          accessorKey: "thresholdExceededCostLastPeriod",
          Cell: ({ cell }: any) => {
            const value = cell.getValue() as string;
            if (isNullOrUndefined(value)) {
              return EMPTY_CELL_VALUE;
            }
            return (
              <Box
                sx={{
                  alignItems: "center",
                  textOverflow: "ellipsis",
                  whiteSpace: "normal",
                  fontSize: "14px",
                  fontWeight: 400,
                  fontFamily: "Manrope",
                }}
              >
                {String(value)}
              </Box>
            );
          },
          size: 180,
        },
        {
          header: "Sequential Threshold Last Year",
          Header: <div>Sequential Threshold Last Year</div>,
          accessorKey: "thresholdExceededLastYear",
          Cell: ({ cell }: any) => {
            const value = cell.getValue() as string;
            if (isNullOrUndefined(value)) {
              return EMPTY_CELL_VALUE;
            }
            return (
              <Box
                sx={{
                  alignItems: "center",
                  textOverflow: "ellipsis",
                  whiteSpace: "normal",
                  fontSize: "14px",
                  fontWeight: 400,
                  fontFamily: "Manrope",
                }}
              >
                {String(value)}
              </Box>
            );
          },
          size: 180,
        },
        {
          header: "Estimate Source",
          Header: <div>Estimate Source</div>,
          accessorKey: "estimateSource",
          Cell: ({ cell }: any) => {
            const value = cell.getValue() as string;
            if (isNullOrUndefined(value)) {
              return EMPTY_CELL_VALUE;
            }
            return (
              <Box
                sx={{
                  alignItems: "center",
                  textOverflow: "ellipsis",
                  whiteSpace: "normal",
                  fontSize: "14px",
                  fontWeight: 400,
                  fontFamily: "Manrope",
                }}
              >
                {value}
              </Box>
            );
          },
          size: 180,
        },
        {
          header: "Unit Cost",
          Header: <div>Unit Cost</div>,
          accessorKey: "unitCost",
          Cell: ({ cell }: any) => {
            const value = cell.getValue() as string;
            if (isNullOrUndefined(value)) {
              return EMPTY_CELL_VALUE;
            }
            return (
              <Box
                sx={{
                  alignItems: "center",
                  textOverflow: "ellipsis",
                  whiteSpace: "normal",
                  fontSize: "14px",
                  fontWeight: 400,
                  fontFamily: "Manrope",
                }}
              >
                {String(value)}
              </Box>
            );
          },
          size: 180,
        },
        {
          header: "Usage Per Unit Area",
          Header: <Box>Usage Per Unit Area</Box>,
          accessorKey: "usagePerUnitArea",
          size: 180,
          Cell: ({ cell }: any) => {
            const value = cell.getValue();
            if (isNullOrUndefined(value)) {
              return <Box>{EMPTY_CELL_VALUE}</Box>;
            }
            return (
              <Tooltip title={value as string}>
                <Box
                  sx={{
                    alignItems: "center",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    paddingLeft: "4px",
                  }}
                >
                  <span>
                    {toUSNumber(value as string)}{" "}
                  </span>
                </Box>
              </Tooltip>
            );
          },
        },
        {
          header: "Bill Superseded",
          Header: <div>Bill Superseded</div>,
          accessorKey: "billSuperceeded",
          Cell: ({ cell }: any) => {
            const value = cell.getValue() as string;
            if (isNullOrUndefined(value)) {
              return EMPTY_CELL_VALUE;
            }
            return (
              <Box
                sx={{
                  alignItems: "center",
                  textOverflow: "ellipsis",
                  whiteSpace: "normal",
                  fontSize: "14px",
                  fontWeight: 400,
                  fontFamily: "Manrope",
                }}
              >
                {String(value)}
              </Box>
            );
          },
          size: 180,
        },
        {
          header: "Data Entries Status",
          Header: <div>Data Entries Status</div>,
          accessorKey: "statusFilter",
          Cell: ({ cell }: any) => {
            const value = cell.getValue() as string;
            if (isNullOrUndefined(value)) {
              return EMPTY_CELL_VALUE;
            }
            return (
              <Box
                sx={{
                  alignItems: "center",
                  textOverflow: "ellipsis",
                  whiteSpace: "normal",
                  fontSize: "14px",
                  fontWeight: 400,
                  fontFamily: "Manrope",
                }}
              >
                {String(value)}
              </Box>
            );
          },
          size: 180,
        },
        {
          header: "",
          Header: <Box sx={{ paddingLeft: "3px" }}>Anomaly log</Box>,
          accessorKey: "anomalies",
          Cell: ({ cell, row }: any) => {
            const anomalies = cell.getValue() as Anomaly[];
            const { manuallyApproved, updateDate } = row.original;

            return (
              <AnomalyLogCell
                anomalies={anomalies}
                updateDate={updateDate}
                isManuallyApproved={manuallyApproved}
              />
            );
          },
          enableSorting: false,
          size: 125,
        },
      ]
    },
  ];
